import axios, {
	AxiosRequestConfig,
	AxiosResponse,
	GenericAbortSignal,
} from 'axios'

import { getApiClient } from '@/api'
import { API_ROUTES } from '@/constants'

import {
	TR2FileData,
	TR2PresignedUrl,
	TRANSFORMATION_STATUS,
	TUploadedMedia,
} from './mediaStorageTypes'

const isTransformationApplied = (
	transformationStatus?: TRANSFORMATION_STATUS,
) =>
	transformationStatus === TRANSFORMATION_STATUS.transformed ||
	transformationStatus === TRANSFORMATION_STATUS.transformedFfmpeg

const normalizeString = (str: string): string => str.normalize('NFC')

export const normalizeFileName = (fileName: string): string =>
	fileName.normalize('NFC')

export const sanitizeFileName = (fileName: string): string => {
	const sanitized = normalizeFileName(fileName)

	const lastDotIndex = sanitized.lastIndexOf('.')
	const extension = lastDotIndex > 0 ? sanitized.substring(lastDotIndex) : ''
	const nameWithoutExt =
		lastDotIndex > 0 ? sanitized.substring(0, lastDotIndex) : sanitized

	let cleanedName = nameWithoutExt.replace(/[^a-zA-Z0-9\s._-]/g, '-')

	cleanedName = cleanedName.replace(/[-\s]+/g, '-')

	cleanedName = cleanedName.replace(/^-+|-+$/g, '')

	if (!cleanedName) {
		cleanedName = 'file'
	}

	const maxLength = 200
	const fullName = cleanedName + extension
	if (fullName.length > maxLength) {
		const maxNameLength = maxLength - extension.length
		cleanedName = cleanedName.substring(0, maxNameLength)
	}

	return cleanedName + extension
}

const renameFile = (file: File, newName: string): File =>
	new File([file], newName, { type: file.type })

export const buildResourceUrl = (resource: TUploadedMedia): string => {
	const key = encodeURIComponent(resource.key ?? resource.publicId)
	return `${resource.bucketRootUrl}/${key}${isTransformationApplied(resource.hasAutoTransformation) ? '/auto' : ''}`
}

export const transformUploadedFileNameForDisplay = (
	fileName: string,
): string => {
	if (!fileName.includes('/')) return fileName
	const afterSlash = fileName.split('/')[1] ?? ''
	const underscoreCount = (afterSlash.match(/_/g) || []).length
	if (underscoreCount < 2) return afterSlash
	const parts = afterSlash.split('_')
	return parts.slice(0, parts.length - 2).join('_')
}

export const uploadFile = async (
	file: File,
	onProgress?: (progress: number) => void,
	abortSignal?: GenericAbortSignal,
	onUploadedFileNameAvailable?: (name: string) => void,
	options?: { isLogoUpload?: boolean; displayFileName?: string },
): Promise<TUploadedMedia | null> => {
	const { data: r2UploadConfig }: AxiosResponse<TR2PresignedUrl> =
		await getApiClient().get(API_ROUTES.mediaStorage.uploadConfig, {
			params: {
				timestamp: Date.now(),
				fileName: options?.displayFileName ?? file.name,
			},
		})
	if (!r2UploadConfig) throw new Error('No R2 upload config.')
	const { presignedUrl, id, key } = r2UploadConfig
	onUploadedFileNameAvailable?.(key)

	const renamedFile = renameFile(file, normalizeString(file.name || ''))
	const config: AxiosRequestConfig = {
		onUploadProgress: (progressEvent) => {
			const percentCompleted = Math.round(
				(progressEvent.loaded * 100) / (progressEvent?.total || 0),
			)
			onProgress?.(percentCompleted)
		},
		signal: abortSignal,
		headers: {
			'Content-Type': file.type,
		},
	}

	let isAborted = false

	try {
		await axios.put(presignedUrl, renamedFile, config)
	} catch (error) {
		// @ts-expect-error unknown error type
		if (error.name === 'CanceledError' || error.name === 'AbortError') {
			isAborted = true
		} else {
			console.error('File upload to R2 failed:', error)
			throw new Error('Failed file upload to R2')
		}
	}

	if (isAborted) {
		return null
	}

	// Report upload
	const { data: fileData }: AxiosResponse<TR2FileData> =
		await getApiClient().post(API_ROUTES.mediaStorage.reportUpload, {
			publicId: key,
			resourceType: file.type,
			externalId: id,
			...(options?.isLogoUpload && { isLogoUpload: true }),
		})
	return {
		key: fileData?.key,
		publicId: fileData?.key,
		resourceType: file.type,
		format: fileData?.format,
		bytes: fileData?.bytes,
		storageProvider: fileData?.storageProvider,
		bucketRootUrl: fileData?.bucketRootUrl,
	}
}
